import axios from "axios";
import Delaunator from "delaunator";
import {
  Box2,
  Box3,
  Box3Helper,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  Line,
  LineBasicMaterial,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Ray,
  Vector2,
  Vector3,
} from "three";
import { PolygonUtils } from "../utils/math-utils";
import { Viewer } from "../viewer";
import { config } from "../../config/config";
import { SCENE_TYPE } from "../config/ViewerConfig";

export interface IVolumeInfo {
  fillVolume: number;
  cutVolume: number;
  minHeightOffset: number;
  maxHeightOffset: number;
}

class HeightMap {
  public heightBuffer: Float32Array;
  public boundingBox: Box3;
  public heightOffset: number;
  public gridSize: number;

  private rowCount: number;
  public colCount: number;

  constructor(
    heights: Float32Array,
    bbox: Box3,
    gridSize: number,
    offset: number = 0
  ) {
    this.heightBuffer = heights;
    this.boundingBox = bbox;
    this.gridSize = gridSize;
    this.heightOffset = offset;
    this.rowCount = Math.round(
      (this.boundingBox.max.y - this.boundingBox.min.y) / gridSize
    );
    this.colCount = Math.round(
      (this.boundingBox.max.x - this.boundingBox.min.x) / gridSize
    );
  }

  public getHeight(x: number, y: number): number {
    const xIndex = Math.floor((x - this.boundingBox.min.x) / this.gridSize);
    const yIndex = Math.floor((y - this.boundingBox.min.y) / this.gridSize);
    return this.heightBuffer[yIndex * this.colCount + xIndex];
  }

  private constructPolygonGridBox(polygonBbox: Box3, offset: Vector3): Box2 {
    const offsetBox = polygonBbox.clone().translate(offset);
    const gridBBox_minx =
      Math.floor(offsetBox.min.x / this.gridSize) * this.gridSize;
    const gridBBox_miny =
      Math.floor(offsetBox.min.y / this.gridSize) * this.gridSize;
    const gridBBox_maxx =
      Math.ceil(offsetBox.max.x / this.gridSize) * this.gridSize;
    const gridBBox_maxy =
      Math.ceil(offsetBox.max.y / this.gridSize) * this.gridSize;

    const polygonGridBox = new Box2().setFromPoints([
      new Vector2(gridBBox_minx, gridBBox_miny),
      new Vector2(gridBBox_maxx, gridBBox_maxy),
    ]);
    polygonGridBox.translate(new Vector2(-offset.x, -offset.y));
    return polygonGridBox;
  }

  public calculateVolume(
    polygonMesh: {
      vertices: Vector3[];
      indices: number[];
    },
    offset: Vector3
  ): IVolumeInfo {
    // grid is in XYPlane
    // points should be mapped to xyPlane
    const polygonBbox = new Box3().setFromPoints(polygonMesh.vertices);
    // const bbbox = new Box3Helper(polygonBbox, new Color(0xffff00));
    // ((window as any)["viewer"] as Viewer)
    //   .getScene(SCENE_TYPE.BACKGROUND)
    //   .add(bbbox);

    // const tbbox = new Box3Helper(this.boundingBox, new Color(0xffffff));
    // ((window as any)["viewer"] as Viewer)
    //   .getScene(SCENE_TYPE.BACKGROUND)
    //   .add(tbbox);

    const polygonGridBBox = this.constructPolygonGridBox(polygonBbox, offset);
    // console.log("polygonGridBBox", polygonGridBBox);

    // const pbbox = new Box3Helper(
    //   new Box3(
    //     new Vector3(polygonGridBBox.min.x, polygonGridBBox.min.y, -0.5),
    //     new Vector3(polygonGridBBox.max.x, polygonGridBBox.max.y, 0.5)
    //   ),
    //   new Color(0xd3aa36)
    // );
    // ((window as any)["viewer"] as Viewer)
    //   .getScene(SCENE_TYPE.BACKGROUND)
    //   .add(pbbox);

    const polygon2d = polygonMesh.vertices.map((p) => new Vector2(p.x, p.y));

    const ray = new Ray(new Vector3(), new Vector3(0, 0, 1));
    const inverseRay = new Ray(new Vector3(), new Vector3(0, 0, -1));
    const getHeightDifferenceToPolygonMesh = (point: Vector3) => {
      ray.origin.copy(point);
      inverseRay.origin.copy(point);
      if (
        ray.intersectsBox(polygonBbox) ||
        inverseRay.intersectsBox(polygonBbox)
      ) {
        let hit = PolygonUtils.intersectionTest(polygonMesh, ray);
        if (!hit) {
          hit = PolygonUtils.intersectionTest(polygonMesh, inverseRay);
        }
        if (hit) {
          // const line = new Line(
          //   new BufferGeometry().setFromPoints([point, hit]),
          //   new LineBasicMaterial({ color: 0xffff00 })
          // );
          // ((window as any)["viewer"] as Viewer)
          //   .getScene(SCENE_TYPE.BACKGROUND)
          //   .add(line);
          return point.z - hit.z;
        }
      }
    };

    let fillVolume = 0;
    let cutVolume = 0;
    let minHeightOffset = Number.MAX_SAFE_INTEGER;
    let maxHeightOffset = Number.MIN_SAFE_INTEGER;

    //center of cells
    const xCenterStart = polygonGridBBox.min.x + this.gridSize / 2;
    const yCenterStart = polygonGridBBox.min.y + this.gridSize / 2;
    const xCenterEnd = polygonGridBBox.max.x - this.gridSize / 2;
    const yCenterEnd = polygonGridBBox.max.y - this.gridSize / 2;
    for (
      let yCenter = yCenterStart;
      yCenter <= yCenterEnd;
      yCenter += this.gridSize
    ) {
      for (
        let xCenter = xCenterStart;
        xCenter <= xCenterEnd;
        xCenter += this.gridSize
      ) {
        // const box = new Box3Helper(
        //   new Box3().setFromCenterAndSize(
        //     new Vector3(xCenter, yCenter, 0),
        //     new Vector3(this.gridSize, this.gridSize, 0)
        //   ),
        //   new Color(0xfc0fc0)
        // );
        // ((window as any)["viewer"] as Viewer)
        //   .getScene(SCENE_TYPE.BACKGROUND)
        //   .add(box);
        //check if the point is inside polygon
        //get height
        if (
          PolygonUtils.isPointInsidePolygon2D(
            xCenter,
            yCenter,
            polygon2d,
            polygonGridBBox
          )
        ) {
          const height = this.getHeight(xCenter, yCenter);
          if (!isNaN(height)) {
            const diff = getHeightDifferenceToPolygonMesh(
              new Vector3(xCenter, yCenter, height + this.heightOffset)
            );
            if (diff !== undefined) {
              if (diff >= 0) {
                //debug box
                // const box = new Box3Helper(
                //   new Box3().setFromCenterAndSize(
                //     new Vector3(
                //       xCenter,
                //       yCenter,
                //       height + this.heightOffset - diff / 2
                //     ),
                //     new Vector3(this.gridSize, this.gridSize, diff)
                //   ),
                //   new Color(0x00ffff)
                // );
                // console.log(
                //   "center above",
                //   xCenter,
                //   yCenter,
                //   (height + this.heightOffset) / 2
                // );
                // console.log("size above", this.gridSize, this.gridSize, diff);
                // ((window as any)["viewer"] as Viewer)
                //   .getScene(SCENE_TYPE.BACKGROUND)
                //   .add(box);
                fillVolume += diff;
                maxHeightOffset =
                  maxHeightOffset > diff ? maxHeightOffset : diff;
              } else {
                // const box = new Box3Helper(
                //   new Box3().setFromCenterAndSize(
                //     new Vector3(
                //       xCenter,
                //       yCenter,
                //       height + this.heightOffset - diff / 2
                //     ),
                //     new Vector3(this.gridSize, this.gridSize, Math.abs(diff))
                //   ),
                //   new Color(0x0000ff)
                // );
                // console.log(
                //   "center below",
                //   xCenter,
                //   yCenter,
                //   (height + this.heightOffset) / 2
                // );
                // console.log("size below", this.gridSize, this.gridSize, diff);
                // ((window as any)["viewer"] as Viewer)
                //   .getScene(SCENE_TYPE.BACKGROUND)
                //   .add(box);
                cutVolume += Math.abs(diff);
                minHeightOffset =
                  minHeightOffset < diff ? minHeightOffset : diff;
              }
            } else {
              // const diff = getHeightDifferenceToPolygonMesh(
              //   new Vector3(xCenter, yCenter, height + this.heightOffset)
              // );
              // const box = new Box3Helper(
              //   new Box3().setFromCenterAndSize(
              //     new Vector3(xCenter, yCenter, height + this.heightOffset),
              //     new Vector3(this.gridSize, this.gridSize, 0)
              //   ),
              //   new Color(0xff0000)
              // );
              // ((window as any)["viewer"] as Viewer)
              //   .getScene(SCENE_TYPE.BACKGROUND)
              //   .add(box);
              console.warn("Should not be valid");
            }
          }
          // else {
          //   console.log("height is NaN");
          // }
        }
      }
    }
    fillVolume = fillVolume * this.gridSize * this.gridSize; //z * x * y
    cutVolume = cutVolume * this.gridSize * this.gridSize;
    minHeightOffset = minHeightOffset === Number.MAX_SAFE_INTEGER ? NaN : minHeightOffset;
    maxHeightOffset = maxHeightOffset === Number.MIN_SAFE_INTEGER ? NaN : maxHeightOffset;
    return {
      cutVolume,
      fillVolume,
      minHeightOffset,
      maxHeightOffset,
    };
  }
}

export class HeightMapHandler {
  public heightMap: HeightMap;

  private _heightMapMesh: Mesh;

  public async loadHeightMap(
    heightMapBBox: Box3,
    offset: Vector3,
    gridSize: number,
    heightOffSet: number,
    abortController: AbortController,
    type: string,
    projectId: string,
    timestamp: string
  ) {
    try {
      let folderName: string
      if (type === 'simple') { folderName = 'point_cloud_split_upload' }
      else if (type === 'sfm') { folderName = 'point_cloud_split_SfM' }
      else if (type === 'helmert') { folderName = 'point_cloud_split_Helmert' }

      let hostName = config.serverHost.includes('localhost') ? 'http://localhost:9090/quick3d' : config.serverHost;
      const url = `${hostName}/project_${projectId}/${folderName}/heightMap.bin?timestamp=${timestamp}`;

      const response = await axios.get(
        url,
        {
          // withCredentials: true,
          responseType: "arraybuffer",
          signal: abortController.signal,
        }
      );
      const offsetBox = heightMapBBox.clone().translate(offset);
      const heightBuffer = new Float32Array(response.data);
      const gridBBox_minx = Math.floor(offsetBox.min.x / gridSize) * gridSize;
      const gridBBox_miny = Math.floor(offsetBox.min.y / gridSize) * gridSize;
      const gridBBox_minz = Math.floor(offsetBox.min.z / gridSize) * gridSize;
      const gridBBox_maxx = Math.ceil(offsetBox.max.x / gridSize) * gridSize;
      const gridBBox_maxy = Math.ceil(offsetBox.max.y / gridSize) * gridSize;
      const gridBBox_maxz = Math.ceil(offsetBox.max.z / gridSize) * gridSize;
      const heightMapGridBBox = new Box3().setFromArray([
        gridBBox_minx,
        gridBBox_miny,
        gridBBox_minz,
        gridBBox_maxx,
        gridBBox_maxy,
        gridBBox_maxz,
      ]);
      // heightMapGridBBox.applyMatrix4(offsetMatrix);
      //validate the lenght
      const numberOfGridCells =
        ((gridBBox_maxx - gridBBox_minx) * (gridBBox_maxy - gridBBox_miny)) /
        (gridSize * gridSize);
      if (numberOfGridCells !== heightBuffer.length) {
        console.warn("height map doesnot match");
      }
      heightMapGridBBox.translate(offset.clone().negate());
      this.heightMap = new HeightMap(
        heightBuffer,
        heightMapGridBBox,
        gridSize,
        heightOffSet
      );
      //render height Map
      //   this.renderHeightMap(this._heightMap);
    } catch (e) {
      console.warn(e);
    }
  }

  public getVolumeInfoUnderPolygon(
    polygonMesh: {
      vertices: Vector3[];
      indices: number[];
    },
    offset: Vector3
  ): IVolumeInfo {
    return this.heightMap.calculateVolume(polygonMesh, offset);
  }

  public renderHeightMap(heightMap: HeightMap, viewer: Viewer) {
    const boxgroup = new Group();
    const heightMapPointsBuffer: number[] = [];
    const heightMapPoints2D: number[][] = [];
    const heightMapcolors: number[] = [];

    const xCenterStart = heightMap.boundingBox.min.x + heightMap.gridSize / 2;
    const yCenterStart = heightMap.boundingBox.min.y + heightMap.gridSize / 2;
    const xCenterEnd = heightMap.boundingBox.max.x - heightMap.gridSize / 2;
    const yCenterEnd = heightMap.boundingBox.max.y - heightMap.gridSize / 2;

    let i = 0;
    for (
      let yCenter = yCenterStart;
      yCenter <= yCenterEnd;
      yCenter += heightMap.gridSize
    ) {
      for (
        let xCenter = xCenterStart;
        xCenter <= xCenterEnd;
        xCenter += heightMap.gridSize
      ) {
        //check if the point is inside polygon
        //get height
        const height = heightMap.getHeight(xCenter, yCenter);
        if (!isNaN(height)) {
          heightMapPointsBuffer.push(
            xCenter,
            yCenter,
            height + heightMap.heightOffset
          );
          heightMapPoints2D.push([xCenter, yCenter]);
          heightMapcolors.push(1, 1, 0);
          boxgroup.add(
            new Box3Helper(
              new Box3().setFromCenterAndSize(
                new Vector3(xCenter, yCenter, 0),
                new Vector3(
                  heightMap.gridSize,
                  heightMap.gridSize,
                  heightMap.gridSize
                )
              ),
              new Color(0xff0000)
            )
          );
        } else {
          heightMapPointsBuffer.push(xCenter, yCenter, 0); //- heightMap.heightOffset
          heightMapPoints2D.push([xCenter, yCenter]);
          heightMapcolors.push(0, 0, 1);

          boxgroup.add(
            new Box3Helper(
              new Box3().setFromCenterAndSize(
                new Vector3(xCenter, yCenter, 0),
                new Vector3(heightMap.gridSize, heightMap.gridSize, 1)
              ),
              new Color(0x00ffff)
            )
          );
        }
        ++i;
      }
    }

    // console.log("Max index", i);

    const delaunay = Delaunator.from(heightMapPoints2D);
    const geom = new BufferGeometry();
    geom.setAttribute(
      "position",
      new BufferAttribute(new Float32Array(heightMapPointsBuffer), 3)
    );
    geom.setAttribute(
      "color",
      new BufferAttribute(new Float32Array(heightMapcolors), 3)
    );
    geom.setIndex(new BufferAttribute(delaunay.triangles, 1));
    this._heightMapMesh = new Mesh(
      geom,
      new MeshBasicMaterial({
        // color: "yellow",
        vertexColors: true,
        opacity: 0.5,
        transparent: true,
        side: DoubleSide,
      })
    );
    viewer.getScene(SCENE_TYPE.STREAM_POINTCLOUD).add(this._heightMapMesh);
    // viewer.getScene(SCENE_TYPE.STREAM_POINTCLOUD).add(boxgroup);
  }

  public setHeightMapVisibility(visibility: boolean, viewer: Viewer) {
    if (visibility) {
      if (this._heightMapMesh) {
        this._heightMapMesh.visible = true;
      } else {
        this.renderHeightMap(this.heightMap, viewer);
      }
    } else {
      if (this._heightMapMesh) {
        this._heightMapMesh.visible = false;
      }
    }
  }
}
